import { useLazyQuery } from '@apollo/client/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { DOWNLOAD_FILES } from 'queries';
import { sessionTokenState } from 'states/app';
import { notificationState } from 'states/notification';
import { graphQLError } from 'utils/Helpers';

const useDownloadFiles = (setFileIds) => {
  const setNotification = useSetAtom(notificationState);
  const { accessReportId } = useParams();
  const sessionToken = useAtomValue(sessionTokenState);

  const [downloadFileById, { loading, data, error }] =
    useLazyQuery(DOWNLOAD_FILES);

  useEffect(() => {
    if (!loading && data) {
      if (data?.result?.failedFileIds?.length === 0) {
        data.result.files.forEach((file, index) => {
          // Stagger downloads: browsers block/drop rapid, synchronous multi-file downloads
          setTimeout(async () => {
            const url = new URL(file.downloadUrl, window.location.origin);
            url.searchParams.set('sessionToken', sessionToken?.value);

            // HEAD check surfaces server errors without buffering the file body in memory
            try {
              const check = await fetch(url.toString(), { method: 'HEAD' });
              if (!check.ok) {
                setNotification({
                  class: 'notification--alert',
                  text: 'DownloadFailed',
                });
                return;
              }
            } catch {
              setNotification({
                class: 'notification--alert',
                text: 'DownloadFailed',
              });
              return;
            }

            const a = document.createElement('a');
            a.href = url.toString();
            a.download = file.name;
            document.body.appendChild(a);
            a.click();
            a.remove();
          }, index * 200);
        });

        setFileIds([]);
        setNotification({
          text: 'DownloadStarted',
        });
      } else {
        setNotification({
          class: 'notification--alert',
          text: 'DownloadFailed',
        });
      }
    }
  }, [loading, data, setFileIds, setNotification, sessionToken]);

  useEffect(() => {
    error &&
      setNotification({
        class: 'notification--alert',
        text: graphQLError(error),
      });
  }, [error, setNotification]);

  const onDownload = useCallback(
    (fileIds) => {
      downloadFileById({
        variables: {
          sessionToken: sessionToken?.value,
          accessReportId,
          fileIds,
        },
      });
    },
    [accessReportId, downloadFileById, sessionToken]
  );

  return { onDownload, loading };
};

export default useDownloadFiles;
